import { useEffect, useMemo, useRef, useState } from 'react';
import type { JSX } from 'react';
import {
  AlertTriangle,
  BadgeCheck,
  BrickWall,
  Camera,
  Crosshair,
  House,
  LandPlot,
  List,
  X,
} from 'lucide-react';
import { describeBuilding, findBrickClashes } from '@bw/building';
import type { BuildingModel } from '@bw/building';
import type { HouseViewer, RoofCovering, ViewerStats } from '@bw/render-3d';
import { plural } from '../copy.js';

/** How far a pointer may travel and still count as a click, not an orbit. */
const CLICK_SLOP_PX = 4;

/**
 * What the roof can be covered in.
 *
 * Appearance only. The roof's shape, pitch and oversail are solved from the
 * walls under it and none of them changes here — this is what the same roof
 * is made of, which is why it lives with the view rather than with the
 * project. Slate is what a roof arrives in; plain keeps the roof material's
 * colour from the palette and no surface at all, which is worth being able to
 * ask for and a poor thing to start at.
 */
const COVERINGS: readonly { readonly id: RoofCovering; readonly label: string }[] = [
  { id: 'plain', label: 'Plain roof' },
  { id: 'slate', label: 'Slate' },
  { id: 'clay', label: 'Clay tiles' },
];

/** Zero means "whatever the window is"; the rest are fixed output sizes. */
const SIZES: readonly { readonly label: string; readonly width: number; readonly height: number }[] =
  [
    { label: 'This view', width: 0, height: 0 },
    { label: '1920 × 1080', width: 1920, height: 1080 },
    { label: '3840 × 2160 (4K)', width: 3840, height: 2160 },
  ];

/**
 * The house viewer, full screen (phase 2, M6 and M8).
 *
 * A thin shell: it owns a canvas and a lifecycle, and everything else lives in
 * `@bw/render-3d`, which is imported only when this mounts. A session that
 * never opens this view never downloads Three.js.
 */
export function View3D({
  building,
  colourOf,
  roughnessOf,
  roofColour,
  frameColour,
  dark,
  onClose,
  onPickBrick,
  onSaveImage,
}: {
  readonly building: BuildingModel;
  readonly colourOf: (materialId: string) => string;
  readonly roughnessOf: (materialId: string) => number | null;
  readonly roofColour: string;
  readonly frameColour: string;
  readonly dark: boolean;
  readonly onClose: () => void;
  readonly onPickBrick: (wallId: string, brickId: number) => void;
  readonly onSaveImage: (blob: Blob) => void;
}): JSX.Element {
  const rootRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const viewerRef = useRef<HouseViewer | null>(null);
  const pressRef = useRef<{ x: number; y: number } | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'failed'>('loading');
  const [stats, setStats] = useState<ViewerStats | null>(null);
  const [described, setDescribed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [size, setSize] = useState(0);
  // Mortar is what the wall looks like, so it is on; turning it off is how you
  // read the bond, with the joints open straight through to the daylight.
  const [mortar, setMortar] = useState(true);
  // The roof is on because it is part of the building; off is how the top of a
  // gable gets looked at. The ground is on because a building stands on
  // something, and its shadow has to land somewhere.
  const [roof, setRoof] = useState(true);
  const [ground, setGround] = useState(true);
  // Slate: the same one the viewer builds its first roof in, so the select
  // says what is on the model rather than what would be if it were asked.
  const [covering, setCovering] = useState<RoofCovering>('slate');
  const [checks, setChecks] = useState(false);
  const [shown, setShown] = useState<'none' | 'all' | number>('none');

  // Run once for the building, not once per render: the view is read-only, so
  // nothing it checks can change while it is up. Walls only clash where they
  // meet, so the cost follows the number of junctions rather than the number
  // of bricks — a few tens of milliseconds on a whole house.
  const clashes = useMemo(() => findBrickClashes(building), [building]);

  const highlight = (next: 'none' | 'all' | number): void => {
    setShown(next);
    const viewer = viewerRef.current;
    if (viewer === null) return;
    if (next === 'none') {
      viewer.setClashHighlight([]);
      return;
    }
    if (next === 'all') {
      viewer.setClashHighlight(clashes.bricks);
      return;
    }
    const group = clashes.walls[next];
    if (group === undefined) return;
    viewer.setClashHighlight(group.bricks);
    // Marking a corner on the far side of the house helps nobody unless the
    // camera goes to it too.
    viewer.focusOn(group.at);
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    const frame = frameRef.current;
    if (canvas === null || frame === null) return;
    let disposed = false;
    let viewer: HouseViewer | null = null;
    let observer: ResizeObserver | null = null;

    // The one dynamic import that keeps Three.js out of the editor's bundle.
    void import('@bw/render-3d')
      .then((module) => {
        if (disposed) return;
        viewer = new module.HouseViewer(canvas, dark ? module.DARK_VIEWER : module.LIGHT_VIEWER);
        viewerRef.current = viewer;
        viewer.onStats(setStats);
        const resize = (): void => {
          const box = frame.getBoundingClientRect();
          viewer?.resize(box.width, box.height, window.devicePixelRatio || 1);
        };
        resize();
        viewer.setBuilding(building, { colourOf, roughnessOf, roofColour, frameColour });
        viewer.frameBuilding(building);
        viewer.start();
        if (import.meta.env.DEV) {
          void import('../debug.js').then((debug) => debug.setDebugViewer(viewer));
        }
        observer = new ResizeObserver(resize);
        observer.observe(frame);
        setState('ready');
      })
      .catch(() => {
        if (!disposed) setState('failed');
      });

    return () => {
      disposed = true;
      observer?.disconnect();
      viewerRef.current = null;
      if (globalThis.__bw?.viewer === viewer) globalThis.__bw.viewer = null;
      viewer?.dispose();
    };
    // Built once per opening: the view is read-only, so nothing it draws can
    // change while it is up.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Focus the view on opening and give focus back on closing, so the keyboard
  // never ends up somewhere behind a full-screen overlay.
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    frameRef.current?.focus();

    // Contain Tab within the overlay, the way the dialogs do. Without this,
    // tabbing walks into the editor underneath — controls that are still in
    // the document but completely hidden, which is worse than a trap. Escape
    // is the documented way out, so this is containment, not a trap.
    const onTab = (event: KeyboardEvent): void => {
      if (event.key !== 'Tab') return;
      const root = rootRef.current;
      if (root === null) return;
      const focusable = [
        ...root.querySelectorAll<HTMLElement>(
          'a[href], button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])',
        ),
      ];
      if (focusable.length === 0) return;
      const first = focusable[0] as HTMLElement;
      const last = focusable[focusable.length - 1] as HTMLElement;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      } else if (!root.contains(document.activeElement)) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onTab, true);
    return () => {
      document.removeEventListener('keydown', onTab, true);
      previous?.focus?.();
    };
  }, []);

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'Escape') {
      event.preventDefault();
      // Escape closes the description first, then the view: one step back at
      // a time rather than throwing away both.
      if (described) setDescribed(false);
      else onClose();
      return;
    }
    if (viewerRef.current?.handleKey(event.key, event.shiftKey) === true) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  // A click picks a brick; a drag orbits. Telling them apart by distance is
  // what stops every orbit from also jumping back to the drawing.
  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>): void => {
    pressRef.current = { x: event.clientX, y: event.clientY };
  };

  const onPointerUp = (event: React.PointerEvent<HTMLDivElement>): void => {
    const press = pressRef.current;
    pressRef.current = null;
    if (press === null || event.button !== 0) return;
    if (Math.hypot(event.clientX - press.x, event.clientY - press.y) > CLICK_SLOP_PX) return;
    const viewer = viewerRef.current;
    const frame = frameRef.current;
    if (viewer === null || frame === null) return;
    const box = frame.getBoundingClientRect();
    const hit = viewer.pickBrick(event.clientX - box.left, event.clientY - box.top);
    if (hit !== null) onPickBrick(hit.wallId, hit.brickId);
  };

  const save = async (): Promise<void> => {
    const viewer = viewerRef.current;
    if (viewer === null || saving) return;
    const choice = SIZES[size] ?? SIZES[0];
    const width = choice === undefined || choice.width === 0 ? viewer.size.width : choice.width;
    const height = choice === undefined || choice.height === 0 ? viewer.size.height : choice.height;
    setSaving(true);
    try {
      const blob = await viewer.capture(Math.round(width), Math.round(height));
      if (blob !== null) onSaveImage(blob);
    } finally {
      setSaving(false);
    }
  };

  const description = described ? describeBuilding(building) : null;

  return (
    <div className="view3d" ref={rootRef}>
      <div
        className="view3d-frame"
        ref={frameRef}
        tabIndex={0}
        role="application"
        aria-label="The building in three dimensions. Drag to orbit, right-drag or shift-drag to pan, click a brick to open it in the drawing, arrow keys turn, shift and the arrow keys pan, plus and minus zoom, Escape returns."
        onKeyDown={onKeyDown}
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
      >
        <canvas ref={canvasRef} />
      </div>

      <header className="view3d-bar">
        <span className="title">The building</span>
        {state === 'ready' && stats !== null && (
          <span className="stats">
            {plural(stats.bricks)}
            {stats.flintPanels > 0 ? ` · ${stats.flintPanels} flint panels` : ''}
            {stats.roofFaces > 0 ? ` · roof in ${stats.roofFaces} slopes` : ''}
            {stats.joineryParts > 0 ? ` · ${stats.joineryParts} joinery parts` : ''} ·{' '}
            {stats.drawCalls} draw {stats.drawCalls === 1 ? 'call' : 'calls'}
          </span>
        )}
        <span className="spacer" />
        {state === 'ready' && (
          <>
            <label className="visually-hidden" htmlFor="view3d-size">
              Image size
            </label>
            <select
              id="view3d-size"
              className="wallpick"
              value={size}
              onChange={(event) => setSize(Number(event.target.value))}
            >
              {SIZES.map((option, index) => (
                <option key={option.label} value={index}>
                  {option.label}
                </option>
              ))}
            </select>
            <label className="visually-hidden" htmlFor="view3d-covering">
              Roof covering
            </label>
            <select
              id="view3d-covering"
              className="wallpick"
              value={covering}
              onChange={(event) => {
                const next = event.target.value as RoofCovering;
                setCovering(next);
                viewerRef.current?.setRoofCovering(next);
              }}
              title="What the roof is covered in"
            >
              {COVERINGS.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
            <button
              className="btn"
              aria-pressed={mortar}
              onClick={() => {
                const next = !mortar;
                setMortar(next);
                viewerRef.current?.setMortarVisible(next);
              }}
              title={mortar ? 'Hide the mortar joints' : 'Show the mortar joints'}
            >
              <BrickWall size={15} /> <span className="label">Mortar</span>
            </button>
            <button
              className="btn"
              aria-pressed={roof}
              onClick={() => {
                const next = !roof;
                setRoof(next);
                viewerRef.current?.setRoofVisible(next);
              }}
              title={roof ? 'Take the roof off' : 'Put the roof back on'}
            >
              <House size={15} /> <span className="label">Roof</span>
            </button>
            <button
              className="btn"
              aria-pressed={ground}
              onClick={() => {
                const next = !ground;
                setGround(next);
                viewerRef.current?.setGroundVisible(next);
              }}
              title={ground ? 'Hide the ground' : 'Show the ground'}
            >
              <LandPlot size={15} /> <span className="label">Ground</span>
            </button>
            <button className="btn" onClick={() => void save()} disabled={saving} title="Save a still">
              <Camera size={15} />{' '}
              <span className="label">{saving ? 'Saving…' : 'Save image'}</span>
            </button>
            <button
              className={clashes.walls.length > 0 ? 'btn warn' : 'btn'}
              onClick={() => {
                setChecks((open) => !open);
                setDescribed(false);
              }}
              aria-expanded={checks}
              title={
                clashes.walls.length === 0
                  ? 'Checks: no walls run into each other'
                  : `Checks: ${clashes.walls.length} places where walls run into each other`
              }
            >
              {clashes.walls.length > 0 ? <AlertTriangle size={15} /> : <BadgeCheck size={15} />}{' '}
              <span className="label">
                {clashes.walls.length === 0 ? 'Checks' : `Checks · ${clashes.walls.length}`}
              </span>
            </button>
            <button
              className="btn"
              onClick={() => {
                setDescribed((open) => !open);
                setChecks(false);
              }}
              aria-expanded={described}
              title="Describe the building"
            >
              <List size={15} /> <span className="label">Describe</span>
            </button>
          </>
        )}
        <button className="btn" onClick={onClose} aria-label="Close the 3D view">
          <X size={15} /> <span className="label">Close</span>
        </button>
      </header>

      {checks && (
        <aside className="view3d-describe" aria-label="Checks on the building">
          <h2>Where walls run into each other</h2>
          {clashes.walls.length === 0 ? (
            <p className="readout">
              No two walls share the same space. Checked {clashes.tested.toLocaleString()} pairs of
              bricks at {building.overlaps.length}{' '}
              {building.overlaps.length === 1 ? 'junction' : 'junctions'}.
            </p>
          ) : (
            <>
              <p className="readout">
                {clashes.bricks.length} bricks in {clashes.walls.length}{' '}
                {clashes.walls.length === 1 ? 'place' : 'places'} share space with a brick in
                another wall.
              </p>
              {/* Walls are placed exactly as drawn and left to interpenetrate —
                  that is a recorded decision, not a bug, so this reports rather
                  than offers to fix. What it adds is which corners, and by how
                  much: a wall meeting another at its centreline runs half a
                  thickness in and always will. */}
              <p className="note">
                Walls are placed exactly as drawn, so these are left as they are. A corner where
                two walls meet at their centrelines runs half a wall thickness in.
              </p>
              <div className="actions">
                <button
                  className="btn"
                  aria-pressed={shown === 'all'}
                  onClick={() => highlight(shown === 'all' ? 'none' : 'all')}
                >
                  {shown === 'all' ? 'Clear highlight' : 'Highlight all'}
                </button>
              </div>
              <ul className="clashlist">
                {clashes.walls.map((group, index) => (
                  <li key={`${group.wallA}|${group.wallB}`}>
                    <b>
                      {group.nameA} · {group.nameB}
                    </b>
                    <br />
                    <span className="dim">
                      {group.bricks.length} bricks, running {Math.round(group.worstDepthMm)} mm in
                    </span>
                    <button
                      className="btn"
                      aria-pressed={shown === index}
                      onClick={() => highlight(shown === index ? 'none' : index)}
                      title={`Show ${group.nameA} against ${group.nameB} on the model`}
                    >
                      <Crosshair size={14} /> <span className="label">Show</span>
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
        </aside>
      )}

      {description !== null && (
        <aside className="view3d-describe" aria-label="The building, described">
          <h2>The building</h2>
          <p className="readout">{description.summary}</p>
          <p className="readout">{description.roof}</p>
          {description.warnings.map((warning) => (
            <p className="note warn" key={warning}>
              {warning}
            </p>
          ))}
          <ul>
            {description.walls.map((wall) => (
              <li key={wall.id}>
                <b>{wall.name}</b>
                <br />
                <span className="dim">{wall.detail}</span>
                {(wall.openings.length > 0 || wall.panels.length > 0) && (
                  <ul>
                    {wall.openings.map((opening) => (
                      <li key={opening}>{opening}</li>
                    ))}
                    {wall.panels.map((panel) => (
                      <li key={panel}>{panel}</li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        </aside>
      )}

      {state !== 'ready' && (
        <div className="view3d-state" role="status">
          {state === 'loading' ? (
            <p>Building the model…</p>
          ) : (
            <>
              <p>The 3D view could not be loaded.</p>
              <p className="note">
                It is downloaded the first time you open it. Check the connection and try again.
              </p>
            </>
          )}
        </div>
      )}

      {state === 'ready' && (
        <p className="view3d-hint">
          Drag to orbit · Shift-drag to pan · Wheel zooms · Arrows turn, with Shift they pan · Click
          a brick to open it · Esc returns
        </p>
      )}
    </div>
  );
}
